import { updateKeyInStore } from 'App';
import { store } from 'app/redux/store';
import { states } from 'states';
import * as Yup from 'yup';
import {
  RentalIncomeStateValues,
  TOtherExpensesItem,
  TRepairsItem,
  updateFieldArrayKey,
  updateKey,
} from './slice';

// predicate:
// 'Displayed in case the user selected "Yes" to the Rental question in the General Subtab in the Income tab.',

export const fields: IBaseField<
  keyof RentalIncomeStateValues | keyof TRepairsItem | keyof TOtherExpensesItem
>[] = [
  {
    name: 'isTheRentalPropertyInTheUS',
    label: 'Is the rental property in the US?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),

    dependencies: [
      {
        name: 'whichStateIsTheRentalPropertyIn',
        label: 'Which state is the rental property in?',
        type: 'state',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
      },
    ],
  },
  {
    name: 'rentalPropertyAddress',
    label: 'Rental Property Address',
    type: 'textarea',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Enter',
  },
  {
    name: 'typeOfProperty',
    label: 'Type of property',
    type: 'select',
    options: [
      {
        label: 'House',
        value: 'House',
      },
      {
        label: 'Apartment',
        value: 'Apartment',
      },
      {
        label: 'Condominum',
        value: 'Condominum',
      },
      {
        label: 'Mobile Home',
        value: 'Mobile Home',
      },
      {
        label: 'Boat',
        value: 'Boat',
      },
      {
        label: 'Land',
        value: 'Land',
      },
      {
        label: 'Other',
        value: 'Other',
      },
    ],
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'costOfLandIncludedInPurchasePrice',
        label: 'Cost of Land included in purchase price',
        type: 'currency',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'House' || value === 'Land',
      },
    ],
  },

  {
    name: 'datePropertyAquiredpurchasedOrInherited',
    label: 'Date Property Aquired (Purchased or Inherited)',
    type: 'date',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Select Date',
  },
  {
    name: 'purchasePrice',
    label: 'Purchase price',
    subLabel: () => (
      <p>If inherited, fair value of property on day of inheritance</p>
    ),
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'dateWhenRentalStarted',
    label: 'Date when rental started',
    type: 'date',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Select Date',
  },
  {
    name: 'numberOfDaysRented365IfFullYear',
    label: 'Number of days rented (365 if full year)',
    type: 'text',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Enter',
  },

  {
    name: 'didYouUseThePropertyYourself',
    label: 'Did you use the property yourself?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'numberOfPersonalUseDays',
        label: 'Number of Personal Use Days',
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'yes',
        placeholder: 'Enter',
      },
    ],
  },
  {
    name: 'doYouOwn100OfTheProperty',
    label: 'Do you own 100% of the property?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'whatIsYourOwnershipPercentageInTheRentalProperty',
        label: 'What is your ownership percentage in the rental property?',
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        predicate: (value) => value === 'no',
        placeholder: 'Enter',
      },
    ],
  },
  {
    name: 'amountOfGrossRentReceivedDuringYear',
    label: 'Amount of gross rent received during the selected year',
    subLabel: () => (
      <p>
        Gross means pre-expenses. Please provide TOTAL amounts; we will prorate
        by your ownership percentage.
      </p>
    ),
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'isThePropertyOwnedJointlyWithYourSpouse',
    label: 'Is the property owned jointly with your spouse?',
    type: 'radio',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    dependencies: [
      {
        name: 'sinceYouAreNotFilingJointlyWhoWillReportTheRentalIncomeAndExpenses',
        label:
          'Since you are not filing jointly, Who will report the rental income and expenses?',
        type: 'select',
        options: [
          { label: 'Myself', value: 'Myself' },
          { label: 'Spouse', value: 'Spouse' },
          { label: 'Split', value: 'Split' },
        ],
        predicate: (value) => value === 'yes',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    dependenciesTitle: 'Rental Expenses',
    name: 'advertising',
    label: 'Advertising',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'autoTravelExpenses',
    label: 'Auto & Travel Expenses',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'cleaningMaintenance',
    label: 'Cleaning & Maintenance',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'commission',
    label: 'Commission',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'managementFees',
    label: 'Management Fees',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'insurance',
    label: 'Insurance',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'legalProfessionalFees',
    label: 'Legal & professional Fees',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'mortgageInterestPaidToFinancialInstitutions',
    label: 'Mortgage Interest Paid to Financial Institutions',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'otherInterest',
    label: 'Other Interest',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'repairsList',
    label: 'Repairs List',
    type: 'fieldArray',
    tooltip: '',
    comment: true,
    effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
    defaultDependency: {
      descriptionOfRepair: null,
      date: null,
      amount: null,
    },
    dependencies: [
      {
        name: 'descriptionOfRepair',
        label: 'Description of repair',
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Enter',
      },
      {
        name: 'date',
        label: 'Date',
        type: 'date',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Select Date',
      },
      {
        name: 'amount',
        label: 'Amount',
        type: 'currency',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'supplies',
    label: 'Supplies',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'howMuchIncomeTaxDidYouPayOnTheRentalIncome',
    label: 'How much income tax did you pay on the rental income?',
    subLabel: () => <p>(do not include any tax paid by the tenant)</p>,
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },

  {
    name: 'howMuchTaxOtherThanIncomeTaxDidYouPaypropertyTaxEtc',
    label:
      'How much tax other than income tax did you pay (Property tax, etc.)?',
    subLabel: () => <p>(do not include any tax paid by the tenant)</p>,
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'utilities',
    label: 'Utilities',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'gardeningYardMaintenance',
    label: 'Gardening & Yard Maintenance',
    type: 'currency',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
  {
    name: 'otherExpensesList',
    label: 'Other Expenses List',
    type: 'fieldArray',
    tooltip: '',
    comment: true,
    effect: (payload) => store.dispatch(updateFieldArrayKey(payload)),
    defaultDependency: { descriptionOfExpense: null, date: null, amount: null },
    dependencies: [
      {
        name: 'descriptionOfExpense',
        label: 'Description of expense',
        type: 'text',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Enter',
      },
      {
        name: 'date',
        label: 'Date',
        type: 'date',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
        placeholder: 'Select Date',
      },
      {
        name: 'amount',
        label: 'Amount',
        type: 'currency',
        tooltip: '',
        comment: true,
        effect: updateKeyInStore(updateKey),
      },
    ],
  },
  {
    name: 'notes',
    label: 'Notes',
    type: 'textarea',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
    placeholder: 'Enter',
  },
  {
    name: 'youCanUploadAnExcelSpreadsheetContainingTheInformationRegardingRentalExpensesInsteadOfCompletingBelow',
    label:
      'You can upload an Excel spreadsheet containing the information regarding rental expenses instead of completing below',
    type: 'upload',
    tooltip: '',
    comment: true,
    effect: updateKeyInStore(updateKey),
  },
];

export const validationSchema = Yup.object().shape({});
